import { create } from "@bufbuild/protobuf";
import IconPlusSmall from "central-icons/IconPlusSmall";
import { Upload } from "lucide-react";
import { useCallback, useContext, useEffect, useMemo, useState, type FC } from "react";

import { withGitpodSize, withLucideIcon } from "@/assets/icons/utils";
import { ErrorFallback } from "@/components/ErrorFallback";
import { Button } from "@/components/flexkit/Button";
import { Text } from "@/components/flexkit/typography/Text";
import { SkeletonBlock } from "@/components/podkit/loading/Skeleton";
import { ImportEnvFileModal } from "@/components/secrets/ImportEnvFileModal";
import { NewSecretModal } from "@/components/secrets/NewSecretModal";
import { SecretsCard } from "@/components/secrets/SecretsCard";
import { SettingsPageContext } from "@/contexts/SettingsPageContext";
import { useDocumentTitle } from "@/hooks/use-document-title";
import { useListUserSecrets, type PlainSecret } from "@/queries/secret-queries";
import { useAuthenticatedUser } from "@/queries/user-queries";
import { type SecretScope, SecretScopeSchema } from "gitpod-next-api/gitpod/v1/secret_pb";

const UploadIcon = withLucideIcon(Upload);

export type UserSecretsPageProps = {
    /** When true, hides the description text (used in modal context where description is provided externally) */
    hideDescription?: boolean;
};

export const UserSecretsPage: FC<UserSecretsPageProps> = ({ hideDescription = false }) => {
    useDocumentTitle("Secrets");

    const { data: secrets, isPending, error } = useListUserSecrets();
    const { data: user, isPending: isUserPending, error: userError } = useAuthenticatedUser();
    const { setBreadCrumbRowAction } = useContext(SettingsPageContext);

    const [showAddSecretModal, setShowAddSecretModal] = useState(false);
    const [showImportEnvModal, setShowImportEnvModal] = useState(false);

    const onNewSecret = useCallback(() => {
        setShowAddSecretModal(true);
    }, [setShowAddSecretModal]);

    const onImportEnv = useCallback(() => {
        setShowImportEnvModal(true);
    }, []);

    const content = useMemo(() => {
        if (error) {
            return <ErrorFallback error={error} />;
        }

        if (isPending || !user) {
            return (
                <SkeletonBlock
                    ready={!isPending && !isUserPending}
                    failed={!!error || !!userError}
                    className="h-[160px] w-full max-w-[800px]"
                />
            );
        }

        const scope = create(SecretScopeSchema, { scope: { case: "userId", value: user?.id } });
        return (
            <UserSecrets
                scope={scope}
                secrets={secrets?.secrets ?? []}
                onNewSecret={onNewSecret}
                hideDescription={hideDescription}
            />
        );
    }, [error, isPending, isUserPending, user, secrets, onNewSecret, userError, hideDescription]);

    useEffect(() => {
        setBreadCrumbRowAction(
            <div className="flex items-center gap-2">
                <Button
                    size="sm"
                    variant="outline"
                    LeadingIcon={UploadIcon}
                    onClick={onImportEnv}
                    data-testid="import-env-modal-trigger"
                    data-tracking-id="import-env-user-secrets-page"
                >
                    Import .env
                </Button>
                <Button
                    size="sm"
                    LeadingIcon={withGitpodSize(IconPlusSmall)}
                    onClick={onNewSecret}
                    data-testid="add-secret-modal-trigger"
                    data-tracking-id="new-secret-user-secrets-page"
                >
                    New Secret
                </Button>
            </div>,
        );

        return () => {
            setBreadCrumbRowAction(null);
        };
    }, [onNewSecret, onImportEnv, secrets?.secrets, setBreadCrumbRowAction]);

    return (
        <div data-testid="user-secrets-page">
            {content}
            {user && (
                <>
                    <NewSecretModal
                        open={showAddSecretModal}
                        enableSecretContainerRegistry={true}
                        scope={create(SecretScopeSchema, { scope: { case: "userId", value: user.id } })}
                        onClose={() => setShowAddSecretModal(false)}
                    />
                    <ImportEnvFileModal
                        open={showImportEnvModal}
                        scope={create(SecretScopeSchema, { scope: { case: "userId", value: user.id } })}
                        onClose={() => setShowImportEnvModal(false)}
                        existingSecrets={secrets?.secrets ?? []}
                    />
                </>
            )}
        </div>
    );
};

const UserSecrets: FC<{
    scope: SecretScope;
    secrets: PlainSecret[];
    onNewSecret: () => void;
    hideDescription?: boolean;
}> = ({ scope, secrets, onNewSecret, hideDescription }) => {
    if (hideDescription) {
        return <SecretsCard scope={scope} secrets={secrets} showCreatorColumn={false} onNewSecret={onNewSecret} />;
    }
    return (
        <div className="flex flex-col gap-6">
            <Text className="text-base text-content-secondary">
                These are your personal secrets that are available in all environments you create.
            </Text>
            <SecretsCard scope={scope} secrets={secrets} showCreatorColumn={false} onNewSecret={onNewSecret} />
        </div>
    );
};
